import { gql } from '@/__generated__';
import {
  CommunicationUpdateTypes,
  ContactPoint,
} from '@/__generated__/graphql';
import { useQuery } from '@apollo/client';
import { env } from 'next-runtime-env';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { logError } from '@/lib/rum';

export enum UserRoles {
  ADMIN = 'admin',
  SOCIAL_MEDIA_MANAGER = 'social_media_manager',
  ORGANIZATION_MANAGER = 'organization_manager',
  GRANTS_MANAGER = 'grant_manager',
}

export type AuthUser = {
  id: string;
  email: string;
  roles: UserRoles[];
};

export type User = Partial<Pick<AuthUser, 'id' | 'roles'>> &
  Pick<AuthUser, 'email'> & {
    updates: Partial<Pick<CommunicationUpdateTypes, 'grants' | 'socialMedia'>>;
  };

export const validateUUID = (uuid: string) => {
  const regexExp =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return regexExp.test(uuid);
};

const fetcher = async (
  input: string | URL | Request,
  init?: RequestInit | undefined
) => {
  const res = await fetch(input, init);
  return res.json();
};

async function postRequest(url: string, { arg }: { arg: any }) {
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(arg),
  });
}

const AUTH_SERVICE_URL = `${env('AUTHENTICATION_DOMAIN')}/authentication/private`;

export function useTenants() {
  const { data, error, isLoading } = useSWR(
    `${AUTH_SERVICE_URL}/tenants`,
    fetcher
  );
  let tenants = [];

  if (data) {
    tenants = data.filter((tenantId: any) => validateUUID(tenantId));
  }

  return {
    tenants,
    isLoading,
    isError: error,
  };
}

export function useCurrentUser() {
  const { data, error, isLoading, mutate } = useSWR(
    `${AUTH_SERVICE_URL}/profile`,
    fetcher
  );

  return {
    user: data,
    isLoading,
    isError: error,
    updateUser: mutate,
  };
}

export async function updateUserSession(tenantId: string) {
  await fetch(`${AUTH_SERVICE_URL}/sessions/${tenantId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function useUsers(tenantId: string, isAdmin?: boolean) {
  const [users, setUsers] = useState<User[]>([]);

  const { data, error, isLoading, mutate } = useSWR(
    tenantId && (isAdmin === undefined || isAdmin === true)
      ? `${AUTH_SERVICE_URL}/tenants/${tenantId}/users`
      : null,
    fetcher
  );
  const [authUsers, setAuthUsers] = useState<AuthUser[]>();
  useEffect(() => {
    if (data && !data.error && Array.isArray(data)) {
      const authUsers: AuthUser[] = data.filter(
        (user: any) => !user.roles?.includes(UserRoles.ADMIN)
      );
      setAuthUsers(authUsers);
    }
  }, [data]);

  const [contactPoints, setContactPoints] = useState<ContactPoint[] | null>();
  const {
    data: contactPointsQueryResponse,
    loading,
    refetch,
  } = useQuery(OrganizationGetContactPointsQuery, {
    variables: { input: { id: tenantId } },
    skip: !tenantId,
  });

  useEffect(() => {
    if (
      contactPointsQueryResponse?.organization?.getById?.__typename ===
        'OrganizationGetByIdSuccess' &&
      contactPointsQueryResponse.organization.getById.result?.contactPoints
    ) {
      setContactPoints(
        contactPointsQueryResponse.organization.getById.result
          .contactPoints as ContactPoint[]
      );
    }
  }, [contactPointsQueryResponse]);

  useEffect(() => {
    if (contactPoints && authUsers) {
      const usersWithCP: User[] = authUsers.map((user: AuthUser) => {
        const match = contactPoints.find((cp) =>
          cp?.communicationChannels.some(
            (channel) => channel.addressee === user.email
          )
        );

        return {
          ...user,
          updates: match?.updates || { grants: false, socialMedia: false },
        };
      });

      const contactsOnly: User[] = contactPoints
        .filter(
          (cp) =>
            !authUsers.some((user) =>
              cp?.communicationChannels.some(
                (channel) => channel.addressee === user.email
              )
            )
        )
        .map((cp) => {
          return {
            email:
              cp?.communicationChannels.find(
                (channel) => channel.type === 'EMAIL'
              )?.addressee || '',
            updates: cp?.updates || { grants: false, socialMedia: false },
          };
        });

      setUsers(
        [...usersWithCP, ...contactsOnly].sort((a: User, b: User) =>
          a.email.localeCompare(b.email)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactPoints, authUsers]);

  const refreshData = async () => {
    await refetch();
    await mutate();
  };

  return {
    users,
    contactPoints,
    isLoading: isLoading || loading,
    isError: error,
    refreshUsers: refreshData,
  };
}

export async function removeUser(tenantId: string, userId: string) {
  await fetch(`${AUTH_SERVICE_URL}/tenants/${tenantId}/users/${userId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

const OrganizationGetContactPointsQuery = gql(`
  query OrganizationGetContactPoints($input: OrganizationGetByIdInput!) {
    organization {
      getById(input: $input) {
        ... on OrganizationGetByIdSuccess {
          result {
            contactPoints {
              updates {
                grants
                socialMedia
              }
              communicationChannels {
                addressee
                type
              }
            }
          }
        }
      }
    }
  }
`);

export function useRoleAdder(tenantId: string, userId: string) {
  const { trigger, data, error, isMutating, reset } = useSWRMutation(
    `${AUTH_SERVICE_URL}/tenants/${tenantId}/users/${userId}/roles`,
    postRequest
  );
  return {
    addUserRoles: trigger,
    data,
    error,
    isMutating,
    reset,
  };
}

export function useUserAdder(tenantId: string) {
  const { trigger, data, error, isMutating, reset } = useSWRMutation(
    `${AUTH_SERVICE_URL}/tenants/${tenantId}/users`,
    postRequest
  );
  return {
    addUser: trigger,
    data,
    error,
    isMutating,
    reset,
  };
}

export async function removeUserRole(
  tenantId: string,
  userId: string,
  role: UserRoles
) {
  await fetch(
    `${AUTH_SERVICE_URL}/tenants/${tenantId}/users/${userId}/roles/${role}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
}

export const hasAdminRole = (userRoles: string[]): boolean => {
  return userRoles.includes(UserRoles.ADMIN);
};

export const hasSocialMediaManagerRole = (userRoles: string[]): boolean => {
  return (
    userRoles.includes(UserRoles.SOCIAL_MEDIA_MANAGER) ||
    hasAdminRole(userRoles)
  );
};

export const hasOrgManagerRole = (userRoles: string[]): boolean => {
  return (
    userRoles.includes(UserRoles.ORGANIZATION_MANAGER) ||
    hasAdminRole(userRoles)
  );
};

export const hasGrantManagerRole = (userRoles: string[]): boolean => {
  return (
    userRoles.includes(UserRoles.GRANTS_MANAGER) || hasAdminRole(userRoles)
  );
};

export const hasManagerOrAdminRole = (userRoles: string[]): boolean => {
  return (
    hasAdminRole(userRoles) ||
    hasSocialMediaManagerRole(userRoles) ||
    hasOrgManagerRole(userRoles) ||
    hasGrantManagerRole(userRoles)
  );
};

export const useGoogleConnectionStatus = (enabled = true) => {
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [connectedGoogleEmail, setConnectedGoogleEmail] = useState('');
  const [isGoogleStatusLoading, setIsGoogleStatusLoading] = useState(true);
  const session = useSessionContext();
  const userId = session.loading ? '' : session.userId;

  useEffect(() => {
    const checkConnection = async () => {
      if (!userId || !enabled) return;

      try {
        const response = await fetch(
          `${env('AUTHENTICATION_DOMAIN')}/gsuite/auth/connected/${userId}`
        );

        if (response.ok) {
          const data = await response.json();
          setIsGoogleConnected(data.isConnected);
          setConnectedGoogleEmail(data.email || '');
        }
      } catch (error) {
        logError('Failed to check Google connection', error);
      } finally {
        setIsGoogleStatusLoading(false);
      }
    };

    if (!session.loading && session.doesSessionExist) {
      checkConnection();
    }
  }, [userId, session, enabled]);

  return { isGoogleConnected, connectedGoogleEmail, isGoogleStatusLoading };
};

export function useImpersonation(tenantId: string) {
  const impersonateUser = async (userIdentifier: string) => {
    await fetch(`${AUTH_SERVICE_URL}/tenants/${tenantId}/impersonate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userIdentifier }),
    });
  };

  const revertImpersonation = async () => {
    await fetch(`${AUTH_SERVICE_URL}/tenants/revert-impersonation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  };

  return {
    impersonateUser,
    revertImpersonation,
  };
}

export function useIsImpersonated() {
  const session = useSessionContext();
  const [isImpersonated, setIsImpersonated] = useState(false);

  useEffect(() => {
    if (!session.loading && session.doesSessionExist) {
      const payload = session.accessTokenPayload as {
        isImpersonated?: boolean;
      };
      setIsImpersonated(!!payload?.isImpersonated);
    }
  }, [session]);

  return isImpersonated;
}
